import React from 'react';

import {
  components,
  DropdownIndicatorProps,
  IndicatorSeparatorProps,
  OnChangeValue,
  ActionMeta,
} from 'react-select';
import { SelectorIcon } from '@heroicons/react/solid';
import { OptionType as Option, CreatableSelectInputProps } from './Types';
import InputLabel from './InputLabel';
import { useTranslation } from 'react-i18next';
import { useCommonSelector } from '@wajeez-web/stores/common';
import CreatableSelect from 'react-select/creatable';

const DropdownIndicator = (props: DropdownIndicatorProps<Option, true>) => {
  return (
    <components.DropdownIndicator {...props}>
      <SelectorIcon className="h-5 w-5 text-gray-400" />
    </components.DropdownIndicator>
  );
};

const indicatorSeparatorStyle = {
  alignSelf: 'stretch',
  backgroundColor: 'white',
  marginBottom: 8,
  marginTop: 8,
  width: 1,
};

const IndicatorSeparator = ({
  innerProps,
}: IndicatorSeparatorProps<Option, true>) => {
  return <span style={indicatorSeparatorStyle} {...innerProps} />;
};

const CreatableSelectInput = ({
  placeholder = 'Select...',
  name,
  value,
  isMulti = undefined,
  options,
  showSwelectIndecator = true,
  onChange,
  label,
  required = false,
  isMenuOpen,
  onCreate,
}: CreatableSelectInputProps) => {
  const handleChange = (
    value: OnChangeValue<Option, true>,
    actionMeta: ActionMeta<Option>
  ) => {
    onChange(name, value);
  };

  const { dir } = useCommonSelector((state) => state.common.settings);

  const { t } = useTranslation();

  return (
    <div>
      {label && <InputLabel text={label} name={name} required={required} />}
      <CreatableSelect
        className="input"
        options={options}
        menuIsOpen={undefined}
        isMulti={isMulti || undefined}
        placeholder={t(
          `inputs.placeholders.${placeholder}`,
          placeholder || ''
        ).toString()}
        name={name}
        defaultMenuIsOpen={isMenuOpen}
        onChange={handleChange}
        onCreateOption={
          onCreate ? (val) => onCreate && onCreate(val) : undefined
        }
        value={value}
        styles={{
          control: (base, state) => ({
            ...base,
            minHeight: '36px !important',
            height: '36px !important',
            padding: '0px !important',
            textAlign: dir === 'ltr' ? 'left' : 'right',
            ' --tw-ring-opacity': 1,
            '--tw-ring-opacity': state.isFocused ? 0.1 : 'default',
            ' --tw-ring-color': state.isFocused
              ? 'var(--theme-ring-color)'
              : 'default',
            '--tw-ring-offset-shadow': state.isFocused
              ? 'var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color)'
              : 'default',
            '--tw-ring-shadow': state.isFocused
              ? 'var(--tw-ring-inset) 0 0 0 calc(4px + var(--tw-ring-offset-width)) var(--tw-ring-color)'
              : 'default',
            boxShadow: state.isFocused
              ? ' var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000)'
              : 'default',
          }),
          container: (base) => ({
            ...base,
            fontSize: '14px !important',
            padding: '0px !important',
          }),
          menu: (provided) => ({
            ...provided,
            position: 'absolute',
            textAlign: dir === 'ltr' ? 'left' : 'right',
            'z-index': '999 !important',
          }),
        }}
        theme={(theme) => ({
          ...theme,
          zIndex: 999,
          border: '0px none',
          width: '100%',
          borderRadius: 6,
          padding: 0,
          margin: 0,
          colors: {
            ...theme.colors,

            primary: 'var(--theme-primary)',
          },
        })}
        components={{ DropdownIndicator, IndicatorSeparator }}
      />
    </div>
  );
};

export default CreatableSelectInput;
